import React, { useMemo } from "react";
import { ShieldAlert, ChevronRight, CircleOff } from "lucide-react";
import { assessAssurance, ASSURANCE_ASSUMPTIONS } from "../../assurance/assurance";
import { coverageSummary } from "../../architecture/coverage";

/* §16 AI Stack Assurance, in the right rail rather than on its own page.
 *
 * Placed here deliberately. An assurance page is a place an operator visits
 * once; an assurance panel beside the risk figures is a thing they cannot
 * avoid reading while acting on them. The brief's question — "can I trust
 * the systems producing that assessment?" — is only useful if it is asked
 * next to the assessment.
 *
 * It leads with findings, and every finding here is bad news. That is the
 * correct output for this platform's current state, and softening it would
 * defeat the purpose of the section.
 */
export default function AssurancePanel({ lang = "en", live, twin, accuracy, timings = [] }) {
  const a = useMemo(
    () => assessAssurance({ liveState: live.state, twin, accuracy, timings }),
    [live.state, twin, accuracy, timings],
  );
  const cov = useMemo(() => coverageSummary(), []);

  const SEV = {
    critical: "border-red-300 bg-red-50 text-red-900",
    high: "border-orange-300 bg-orange-50 text-orange-900",
    medium: "border-amber-200 bg-amber-50 text-amber-900",
    low: "border-slate-200 bg-slate-50 text-slate-700",
  };

  return (
    <div className="border-t border-slate-200 px-3 py-3">
      <div className="mb-2 flex items-center gap-1.5">
        <ShieldAlert size={12} className="text-slate-500" />
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
          {lang === "mr" ? "एआय स्टॅक खात्री" : "AI Stack Assurance"}
        </span>
      </div>

      {/* The answer to the brief's question, before any number. */}
      <p className="mb-2 rounded border border-slate-300 bg-slate-50 px-2 py-1.5 text-[10px] leading-snug text-slate-800">
        {a.trustStatement}
      </p>

      {/* Data availability — one live feed out of sixteen. */}
      <div className="mb-2 grid grid-cols-3 gap-1 text-center">
        {[
          { n: a.dataAvailability.live, l: lang === "mr" ? "थेट" : "Live", cls: "text-emerald-700" },
          { n: a.dataAvailability.simulated, l: lang === "mr" ? "सिम्युलेटेड" : "Simulated", cls: "text-fuchsia-700" },
          { n: a.dataAvailability.absent, l: lang === "mr" ? "अनुपस्थित" : "Absent", cls: "text-red-700" },
        ].map((x) => (
          <div key={x.l} className="rounded border border-slate-200 bg-white py-1">
            <div className={`text-[13px] font-black tabular-nums ${x.cls}`}>{x.n}</div>
            <div className="text-[8px] font-bold uppercase tracking-wide text-slate-400">{x.l}</div>
          </div>
        ))}
      </div>

      {/* Findings, most severe first. */}
      <div className="space-y-1">
        {a.findings.slice(0, 5).map((f) => (
          <div key={f.text} className={`rounded border px-2 py-1 ${SEV[f.severity]}`}>
            <div className="text-[8px] font-black uppercase tracking-wider opacity-70">
              {f.severity} · {f.area}
            </div>
            <p className="text-[9px] leading-snug">{f.text}</p>
          </div>
        ))}
      </div>

      {/* The unmeasurable, stated as such. */}
      <div className="mt-2 rounded border border-dashed border-slate-300 bg-slate-50 px-2 py-1.5">
        <div className="mb-0.5 flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-slate-500">
          <CircleOff size={9} />
          {lang === "mr" ? "मोजता न येणारे" : "Cannot be measured"}
        </div>
        <p className="text-[9px] leading-snug text-slate-600">{a.falseAlerts.reason}</p>
        <p className="mt-1 text-[9px] leading-snug text-slate-500">
          <strong>{lang === "mr" ? "आवश्यक" : "Would need"}:</strong>{" "}
          {a.falseAlerts.whatWouldBeNeeded}
        </p>
      </div>

      {/* Latency — the one genuinely measured performance figure. */}
      {a.latency.measured && (
        <div className="mt-2">
          <div className="mb-1 text-[9px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "इंजिन विलंब (मोजलेला)" : "Engine latency (measured)"}
          </div>
          <div className="flex flex-wrap gap-1">
            {a.latency.timings.map((t) => (
              <span
                key={t.label}
                className={`rounded border px-1 py-0.5 text-[8px] tabular-nums ${
                  t.withinBudget === false
                    ? "border-amber-300 bg-amber-50 text-amber-800"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
              >
                {t.label} <strong>{t.ms}ms</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 22-point coverage, computed from the verified map. */}
      <div className="mt-2 rounded border border-slate-200 bg-white px-2 py-1.5">
        <div className="mb-0.5 text-[9px] font-black uppercase tracking-wider text-slate-400">
          {lang === "mr" ? "आर्किटेक्चर व्याप्ती" : "Architecture coverage"}
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-[15px] font-black tabular-nums text-slate-900">{cov.built}</span>
          <span className="text-[9px] text-slate-500">
            / {cov.total} {lang === "mr" ? "पूर्ण" : "built"} · {cov.partial}{" "}
            {lang === "mr" ? "अंशतः" : "partial"} · {cov.narrative}{" "}
            {lang === "mr" ? "वर्णनात्मक" : "narrative"}
          </span>
        </div>
        <p className="mt-0.5 text-[9px] leading-snug text-slate-500">{cov.statement}</p>
      </div>

      <details className="mt-2">
        <summary className="flex cursor-pointer items-center gap-0.5 text-[9px] font-bold text-slate-400 hover:text-slate-600">
          <ChevronRight size={8} />
          {lang === "mr" ? "गृहीतके" : "Method"}
        </summary>
        <ul className="mt-1 space-y-0.5">
          {ASSURANCE_ASSUMPTIONS.map((x) => (
            <li key={x} className="text-[9px] leading-snug text-slate-500">· {x}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
